import React from 'react';
import {
  CSpinner,
  CButton,
  CFormSelect,
} from '@coreui/react-pro';
import {
  useDispatch,
  useSelector,
  useStore,
} from 'react-redux';
import {fetchContangoEmbedToken} from './marketingPlan-actions';
import {
  fetchCashBidLocationsWithoutFutures,
  userSelectedCashBidLocationWithoutFutures,
  fetchCashBidLocationAndShowModal,
} from '../agronomy/agronomy-actions';
import CashBidsModal from '../cash-bids/CashBidsModal';
import PurchaseContractModal from '../cash-bids/PurchaseContractModal';
import styles from './MarketingPlan.module.css';


const CONTANGO_SCRIPT_ID = 'contango-embed-loader';

const EMBED_CONTAINER_ID = 'contango-farm-plan';

const EMBED_ROWS = [
  ['farm-plan-header', 'inputs'],
  ['position-summary', 'selling-targets'],
  ['position-detail'],
  ['carry-over-time', 'prices-over-time'],
];

const MarketingPlan = () => {

  const dispatch = useDispatch();
  const store = useStore();

  const handleRef = React.useRef(null);

  const {
    fetchingEmbedToken,
    embedToken,
    embedTokenFailed,
  } = useSelector(state => state.marketingPlan);

  const {
    locations,
    locationsDropDownListDataSource,
    selectedLocation,
  } = useSelector(state => state.agronomy);

  React.useEffect(() => {
    dispatch(fetchContangoEmbedToken());
    dispatch(fetchCashBidLocationsWithoutFutures(false));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasEmbedToken = Boolean(embedToken);

  React.useEffect(() => {
    if (!hasEmbedToken) {
      return undefined;
    }
    let cancelled = false;

    const mountWidgets = () => {
      if (cancelled || !window.ContangoEmbed) {
        return;
      }
      const currentToken = store.getState().marketingPlan.embedToken;
      const handle = window.ContangoEmbed.mount('#' + EMBED_CONTAINER_ID, {
        token: currentToken,
        rows: EMBED_ROWS,
        onTokenExpired: async () => {
          await dispatch(fetchContangoEmbedToken());
          return store.getState().marketingPlan.embedToken;
        },
      });
      if (handle) {
        handleRef.current = handle;
      }
    };

    let script = document.getElementById(CONTANGO_SCRIPT_ID);
    if (window.ContangoEmbed) {
      mountWidgets();
    }
    else if (script) {
      script.addEventListener('load', mountWidgets);
    }
    else {
      script = document.createElement('script');
      script.id = CONTANGO_SCRIPT_ID;
      script.src = 'https://embed.contango.live/contango-embed-loader.js';
      script.addEventListener('load', mountWidgets);
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (script) {
        script.removeEventListener('load', mountWidgets);
      }
      if (handleRef.current && typeof handleRef.current.destroy === 'function') {
        handleRef.current.destroy();
      }
      handleRef.current = null;
    };
  }, [hasEmbedToken]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.container}>
      {fetchingEmbedToken && (
        <div className={styles.spinnerDiv}>
          <CSpinner color='dark' />
        </div>
      )}
      {embedTokenFailed && (
        <div className={styles.errorDiv}>
          <p className={styles.errorText}>Unable to load Marketing Plan. Please try again later.</p>
        </div>
      )}
      {embedToken && (
        <>
          <div className={styles.toolbar}>
              <CButton
                color='primary'
                variant='outline'
                style={{width: 140, height: 58}}
                onClick={() => {
                  if (Object.keys(selectedLocation).length === 0) {
                    alert('You must select a location to sell grain.');
                  }
                  else {
                    dispatch(fetchCashBidLocationAndShowModal());
                  }
                }}>
                Sell Grain
              </CButton>
              <CFormSelect
                floatingLabel='Select Location to Sell Grain'
                style={{width: 300, lineHeight: 4}}
                value={selectedLocation.locationId}
                options={locationsDropDownListDataSource}
                onChange={e => {
                  var index = locations.findIndex(location => location.locationId.toString() === e.target.value);
                  dispatch(userSelectedCashBidLocationWithoutFutures(locations[index], false));
                }}
              />
            </div>
          <div className={styles.embedLayout}>
            <div id={EMBED_CONTAINER_ID} />
          </div>
        </>
      )}
      <CashBidsModal />
      <PurchaseContractModal />
    </div>
  );
};

export default MarketingPlan;
